/**
 * Inline session picker for the Projects agent.
 *
 * Used when the agent needs the clinician to choose which session to act
 * on (e.g. "Which session's note would you like to push to your EHR?"):
 * it asks ``ask_user`` with ``kind: 'pick_session'`` and no options, and
 * this list renders the user's recent sessions (self-fetched, searchable)
 * instead of a blind free-text box. Supports single OR multi select.
 *
 * Selection is owned by the parent (the clarification's ``selectedIds`` +
 * ``toggleOption``) so it plugs straight into the existing single/multi
 * answer machinery; this component just renders rows and reports toggles.
 *
 * The rows come from the app's review-context sources, so they cross as the
 * ``useLinkableItems`` hook port; the search query stays here, as picker state.
 */

import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { LinkableContextItems } from '../../../data/projects/useLinkableContextItems';
import { useLinkableContextItems } from '../hooks/useLinkableContextItems';
import { SessionMetaRow } from './SessionMetaRow';

export interface PickedSession {
  id: string;
  name: string;
}

export interface SessionPickerListProps {
  /** Ids currently selected (drives the row highlight / check). */
  selectedIds: ReadonlySet<string>;
  /** Toggle a session in/out of the selection (parent enforces the cap). */
  onToggle: (session: PickedSession) => void;
  disabled?: boolean;
  className?: string;
}

/** What the app supplies: the recent-session rows for a search query. */
export interface SessionPickerListHost {
  /** Called unconditionally with the picker's query. Module-level, so its identity is stable. */
  useLinkableItems: (args: {
    query: string;
    limit: number;
  }) => Pick<LinkableContextItems, 'sessionItems'>;
}

// A glance surface, but roomier than the @-mention default so the user
// can find a session without always searching.
const SESSION_LIMIT = 25;

export const SessionPickerListView = ({
  selectedIds,
  onToggle,
  disabled = false,
  className,
  useLinkableItems,
}: SessionPickerListProps & SessionPickerListHost): JSX.Element => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const { sessionItems } = useLinkableItems({ query, limit: SESSION_LIMIT });

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid="session-picker">
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        placeholder={intl.formatMessage({
          id: 'mH7XXju/eA',
          defaultMessage: 'Search sessions by name or patient',
        })}
        aria-label={intl.formatMessage({
          defaultMessage: 'Search sessions',
          id: 'LlbgR0iPuc',
          description: 'Accessible label for the session search field',
        })}
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
      />

      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto [scrollbar-gutter:stable]">
        {sessionItems.length === 0 ? (
          <TypographyCaption className="px-3 py-4 text-text-secondary">
            {query.trim() ? (
              <FormattedMessage
                defaultMessage="No sessions match your search."
                id="zIkM7jeJEl"
                description="Empty state shown when a session search has no matches"
              />
            ) : (
              <FormattedMessage
                defaultMessage="No recent sessions to choose from."
                id="gDJKA3l+G7"
                description="Empty state shown when there are no recent sessions"
              />
            )}
          </TypographyCaption>
        ) : (
          sessionItems.map((item) => {
            // ``sessionItems`` is always the session variant from the hook.
            if (item.type !== 'session') return null;
            const isSelected = selectedIds.has(item.sessionId);
            return (
              <SessionMetaRow
                key={item.sessionId}
                disabled={disabled}
                onClick={() => onToggle({ id: item.sessionId, name: item.sessionName })}
                aria-pressed={isSelected}
                selected={isSelected}
                sessionName={item.sessionName}
                patientName={item.patientName}
                trailing={
                  isSelected ? (
                    <Check className="size-4 shrink-0 text-text-active" aria-hidden />
                  ) : undefined
                }
                data-testid="session-picker-row"
              />
            );
          })
        )}
      </div>
    </div>
  );
};

/** Binds the session picker to the linkable-context rows. */
export const SessionPickerList = (props: SessionPickerListProps): JSX.Element => (
  <SessionPickerListView {...props} useLinkableItems={useLinkableContextItems} />
);
